import { useState, useMemo, useRef, useEffect } from 'react'
import Paper from '@/components/Paper'
import BarChart from '@/components/Analytic/BarChart'
import { SCHEMES } from '@/components/EchartCustomized'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { SalesTargetFilters } from '@/modules/Report/components/BusinessTargets/types'
import {
    getEmployeeDashboard,
    getEmployeesByIds,
} from '@/modules/Report/services'
import { formatMoney } from '@/modules/Report/components/TargetAnalytics/helpers'
import { abbreviateName } from '@/utils/function'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import usePermissionsStore from '@/stores/usePermissionsStore'
import useAuthStore from '@/stores/useAuthStore'

// type PeriodType = 'month' | 'quarter' | 'year'

// const periods: { value: PeriodType; label: string }[] = [
//     { value: 'month', label: 'target_analytics.common.month' },
//     { value: 'quarter', label: 'target_analytics.common.quarter' },
//     { value: 'year', label: 'target_analytics.common.year' },
// ]

interface SalePerformanceBarChartProps {
    filters: SalesTargetFilters
}

export default function SalePerformanceBarChart({
    filters,
}: SalePerformanceBarChartProps) {
    const { t } = useTranslation('report')
    const { isAdmin } = usePermissionsStore()
    const { employee } = useAuthStore()

    const containerRef = useRef<HTMLDivElement>(null)
    const [chartHeight, setChartHeight] = useState(500)

    const { data: dashboardData, renderState: renderDashboardState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.EMPLOYEE_DASHBOARD, filters],
            queryFn: () =>
                getEmployeeDashboard(
                    filters,
                    !isAdmin ? employee?.employee_id : undefined
                ),
            enabled:
                !!filters.employee_id &&
                filters.employee_id.length > 0 &&
                !!filters.gte &&
                !!filters.lte,
        })

    // Fetch employee details (for department names)
    const { data: employeeDetails, renderState: renderEmployeesState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.EMPLOYEES, 'contains', filters.employee_id],
            queryFn: () => getEmployeesByIds(filters.employee_id!),
            enabled: !!filters.employee_id && filters.employee_id.length > 0,
        })

    // Using shared abbreviateName from @/utils/function

    // Transform dashboardData to chart format
    const { chartData, fullNameMap } = useMemo(() => {
        const nameMap = new Map<string, string>()

        if (!dashboardData?.employees || dashboardData.employees.length === 0) {
            if (
                filters.employee_id &&
                filters.employee_id.length > 0 &&
                employeeDetails
            ) {
                const data = employeeDetails.map((emp) => {
                    const abbr = abbreviateName(emp.employee_name)
                    nameMap.set(abbr, emp.employee_name)
                    return { category: abbr, values: [0, 0] }
                })
                return { chartData: data, fullNameMap: nameMap }
            }
            return { chartData: [], fullNameMap: nameMap }
        }

        const data = dashboardData.employees.map((employee) => {
            const abbr = abbreviateName(employee.employee_name)
            nameMap.set(abbr, employee.employee_name)
            return {
                category: abbr,
                values: [employee.actual_revenue, employee.target_amount],
            }
        })
        return { chartData: data, fullNameMap: nameMap }
    }, [dashboardData, filters.employee_id, employeeDetails])

    // Monitor container size
    useEffect(() => {
        if (!containerRef.current) return

        const resizeObserver = new ResizeObserver((entries) => {
            // Use requestAnimationFrame to avoid ResizeObserver loop warning
            requestAnimationFrame(() => {
                for (const entry of entries) {
                    if (entry.contentRect) {
                        // Set height to 80% of parent container
                        setChartHeight(entry.contentRect.height * 0.8)
                    }
                }
            })
        })

        resizeObserver.observe(containerRef.current)

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    // Check if we have data
    const hasData = chartData.length > 0
    const stateComponent = renderDashboardState() || renderEmployeesState()

    return (
        <div ref={containerRef} className="h-full w-full">
            <Paper className="h-full flex flex-col gap-4">
                {/* Header with title and period tabs */}
                <div className="flex items-center justify-between">
                    <Typogaphy className="text-sm font-medium text-[#1A237E]">
                        {t('target_analytics.performance_chart.title')}
                    </Typogaphy>
                </div>

                {/* Loading/Error State */}
                {stateComponent}

                {/* Empty State */}
                {!stateComponent && !hasData && (
                    <div className="flex-1 flex items-center justify-center">
                        <p className="text-xs text-gray-400 italic">
                            {t(
                                'target_analytics.performance_chart.filter_prompt'
                            )}
                        </p>
                    </div>
                )}

                {/* Bar Chart */}
                {!stateComponent && hasData && (
                    <BarChart
                        data={chartData}
                        series={[
                            {
                                name: t(
                                    'target_analytics.performance_chart.actual'
                                ),
                                color: SCHEMES.default[0],
                            },
                            {
                                name: t(
                                    'target_analytics.performance_chart.target'
                                ),
                                color: SCHEMES.default[1],
                            },
                        ]}
                        height={chartHeight}
                        showLegend
                        legendPosition="top"
                        legendIcon="circle"
                        legendItemSize={8}
                        legendGap={0}
                        yAxisFormatter={(value) =>
                            formatMoney(value, {
                                showCurrency: false,
                            })
                        }
                        barGap="10%"
                        barCategoryGap="40%"
                        xAxisLabelRotate={0}
                        gridTop={30}
                        gridBottom={20}
                        gridLeft="3%"
                        gridRight="3%"
                        tooltipFormatter={(params) => {
                            const fullName =
                                fullNameMap.get(params[0]?.name) ||
                                params[0]?.name
                            const items = params
                                .map(
                                    (p) =>
                                        `<div style="display: flex; align-items: center; gap: 8px;">
                                        <span style="display: inline-block; width: 10px; height: 10px; background: ${p.color};"></span>
                                        <span>${p.seriesName} <strong>${formatMoney(
                                            p.value,
                                            {
                                                showCurrency: false,
                                            }
                                        )}</strong></span>
                                    </div>`
                                )
                                .join('')
                            return `<div style="padding: 8px;">
                            <div style="font-weight: 600; margin-bottom: 8px;">${fullName}</div>
                            ${items}
                        </div>`
                        }}
                    />
                )}
            </Paper>
        </div>
    )
}
